import { NetworkError, NetworkErrors } from '../utils/Errors';
import { NETWORK_PING_INTERVAL, NOTIFY_LISTENER_TIMER_VALUE, PING_FILE_PATH } from '../AppConstants';
import { isNullOrUndefined } from '../utils/utils';

export interface NetworkConnectionListener {
  onInternetConnectionStatusChanged(state: InternetState): void;
}

export interface NetworkPingStatusListener {
  onPingServiceStatusUpdate(state: InternetState): void;
}

export enum InternetState {
  NONE,
  INTERNET_CONNECTED,
  INTERNET_DISCONNECTED,
  PING_RESPONSE_SUCCESS,
  PING_RESPONSE_FAIL
}

// Monitors user's network connection status and manages UI accordingly.
export class NetworkConnectionMonitor {

  static INSTANCE: NetworkConnectionMonitor = new NetworkConnectionMonitor();
  connectedToInternet: boolean = navigator.onLine;

  private listeners: Set<NetworkConnectionListener> = new Set<NetworkConnectionListener>();

  private pingStatusListener: Set<NetworkPingStatusListener> = new Set<NetworkPingStatusListener>();
  private waitAndPingAfterRequestMap = new Map();

  private notifyListenerTimer: any = null;
  private pingServiceInterval: number | null = null;

  private currentNotifiedInternetState = InternetState.NONE;

  private constructor() {
    this.connectedToInternet = navigator.onLine;
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
  }

  private onOnline = () => {
    this.connectedToInternet = true;
    this.clearNotifyTimerAndScheduleNotifier();
  }

  private updateListenersForInternetConnectionUpdate = () => {
    this.currentNotifiedInternetState = GetInternetState();
    for (let listener of this.listeners) {
      if (!isNullOrUndefined(listener)) {
        listener.onInternetConnectionStatusChanged(this.currentNotifiedInternetState);
      }
    }
    this.notifyListenerTimer = null;
  }

  private onOffline = () => {
    this.connectedToInternet = false;
    this.clearNotifyTimerAndScheduleNotifier();
  }

  private clearNotifyTimerAndScheduleNotifier() {
    if (!isNullOrUndefined(this.notifyListenerTimer)) {
      clearTimeout(this.notifyListenerTimer);
      this.notifyListenerTimer = null;
    }

    if (this.currentNotifiedInternetState != GetInternetState()) {
      this.notifyListenerTimer = setTimeout(this.updateListenersForInternetConnectionUpdate, NOTIFY_LISTENER_TIMER_VALUE);
    }
  }

  // waitAndPing method
  // 1. wait for some time, see WAIT_TIME_FOR_NETWORK_RECONNECT for duration,
  // 2. If network already reconnected then return true
  // 3. If network NOT connected, then execute ping check to check network connection,
  // 4. return ping result
  // 5. requestId is used to cancel the ongoing waiting request using clearwaitAndPingRequest
  public  waitAndPingAfter(waitingPeriodInMillis: number, requestId: string) {
    var networkConnectionObject = this;
    let timeoutRequest: any = null;
    const cancelRequest = function() {clearTimeout(timeoutRequest);}
    return new Promise(function (resolve) {
        timeoutRequest = setTimeout(function () {
        networkConnectionObject.isPingConnected()
          .then(function (pingConnected: boolean) {
            networkConnectionObject.waitAndPingAfterRequestMap.delete(requestId);
            resolve(pingConnected);
          });
      }, waitingPeriodInMillis);

      networkConnectionObject.waitAndPingAfterRequestMap.set(requestId, cancelRequest);
    });
  }

  /* clearWaitAndPingRequest method : used to remove the waitAndPingAfter request, if still not processed.
  return true, if succesfully removed else false.
  param: requestId string, it should be same as what is sent while requesting waitAndPingAfter */
  public clearWaitAndPingRequest(requestId: string): boolean {
    const cancelRequest = this.waitAndPingAfterRequestMap.get(requestId);
    if (!isNullOrUndefined(cancelRequest)) {
      cancelRequest();
      return this.waitAndPingAfterRequestMap.delete(requestId);
    }
    return false;
  }

  // isPingConnected method
  // returns a promise that checks
  // If app is able to ping a file from server
  public isPingConnected(): Promise<boolean> {
    return new Promise<boolean>(function (resolve) {
      var pingHeader = new Headers();
      pingHeader.append('pragma', 'no-cache');
      pingHeader.append('cache-control', 'no-store');
      fetch(PING_FILE_PATH, {
        method: 'GET',
        // Fetch method to NOT return cached value
        cache: "no-store",
        headers: pingHeader
      })
        .then(resp => {
          if (resp.ok) {
            // Network connected and response returned successfuly
            resolve(true);
          } else {
            // Network connected but response returned with error
            resolve(false);
          }
        })
        .catch(function () {
          // Network NOT connected, hence return false
          resolve(false);
        })
    });

  }

  public StartPingServiceForListener(listener: NetworkPingStatusListener) {
    this.pingStatusListener.add(listener);
    if (isNullOrUndefined(this.pingServiceInterval)) {
      this.pingServiceInterval = window.setInterval(async () => {
        const isPingConnected = IsConnectedToNetwork() && await this.isPingConnected();
        for (let listener of this.pingStatusListener) {
          if (!isNullOrUndefined(listener)) {
            listener.onPingServiceStatusUpdate(isPingConnected ? InternetState.PING_RESPONSE_SUCCESS : InternetState.PING_RESPONSE_FAIL);
          }
        }
      }, NETWORK_PING_INTERVAL);
    }
  }

  public StopPingServiceForListener(listener: NetworkPingStatusListener) {
    this.pingStatusListener.delete(listener);
    if (this.pingStatusListener.size == 0 && !isNullOrUndefined(this.pingServiceInterval)) {
      clearInterval(this.pingServiceInterval!);
      this.pingServiceInterval = null;
    }
  }

  public addListener(listener: NetworkConnectionListener) {
    if (!isNullOrUndefined(listener)) {

      // should notify listener, if internet is already not available before listener register.
      if (!IsConnectedToNetwork()) {
        listener.onInternetConnectionStatusChanged(InternetState.INTERNET_DISCONNECTED);
      }
      this.listeners.add(listener);
    }
  }

  public removeListener(listener: NetworkConnectionListener) {
    if (!isNullOrUndefined(listener)) {
      this.listeners.delete(listener);
    }
  }
}

// Throws error if the user is not online
export function CheckNetworkConnection() {
  if (!IsConnectedToNetwork()) {
    throw new NetworkError(NetworkErrors.NoInternetConnection, "User is not connected to the internet");
  }
}

export function IsConnectedToNetwork() {
  return navigator.onLine && GetNetworkConnectionMonitorInstance().connectedToInternet;
}

export function GetInternetState() {
  return (IsConnectedToNetwork() ? InternetState.INTERNET_CONNECTED : InternetState.INTERNET_DISCONNECTED);
}


export function GetNetworkConnectionMonitorInstance(): NetworkConnectionMonitor {
  return NetworkConnectionMonitor.INSTANCE;
}